import apiClient from './api';

export interface UserAddonAssignment {
  id: string;
  subscriptionId: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  addonId: string;
  assignedAt: string;
  assignedByUserId: string;
}

export interface AddonSeatSummary {
  addonId: string;
  addonDisplayName: string;
  purchasedQuantity: number;
  assignedCount: number;
  availableCount: number;
  assignments: UserAddonAssignment[];
}

export interface AddonAccessRequest {
  id: string;
  tenantId: string;
  userId: string;
  userEmail: string;
  userFullName: string;
  addonId: string;
  status: 'pending' | 'approved' | 'denied';
  requestNote?: string;
  reviewNote?: string;
  requestedAt: string;
  reviewedAt?: string;
}

export interface AddonAccessCheck {
  hasAccess: boolean;
  requiredAddonId?: string;
  hasPendingRequest: boolean;
  accessDeniedReason?: string;
  isTenantGated?: boolean;
}

class AddonAssignmentService {
  /** Admin: get seat usage summary for all add-ons in the tenant */
  async getSeatSummary(tenantId: string): Promise<AddonSeatSummary[]> {
    const r = await apiClient.get(`/api/tenants/${tenantId}/addon-assignments/summary`);
    return r.data;
  }

  /** Admin: assign an add-on seat to a user */
  async assignSeat(tenantId: string, userId: string, addonId: string): Promise<UserAddonAssignment> {
    const r = await apiClient.post(`/api/tenants/${tenantId}/addon-assignments`, { userId, addonId });
    return r.data;
  }

  /** Admin: unassign an add-on seat from a user */
  async unassignSeat(tenantId: string, userId: string, addonId: string): Promise<void> {
    await apiClient.delete(`/api/tenants/${tenantId}/addon-assignments`, { data: { userId, addonId } });
  }

  /** Get add-on IDs assigned to a specific user */
  async getUserAssignedAddonIds(tenantId: string, userId: string): Promise<string[]> {
    const r = await apiClient.get<string[]>(`/api/tenants/${tenantId}/addon-assignments/user/${userId}`);
    return r.data;
  }

  /** User: submit an access request for an add-on */
  async requestAccess(tenantId: string, addonId: string, requestNote?: string): Promise<AddonAccessRequest> {
    const r = await apiClient.post(`/api/tenants/${tenantId}/access-requests`, { addonId, requestNote });
    return r.data;
  }

  /** Admin: get access requests (optionally filtered by status) */
  async getAccessRequests(tenantId: string, status?: 'pending' | 'approved' | 'denied'): Promise<AddonAccessRequest[]> {
    const query = status ? `?status=${status}` : '';
    const r = await apiClient.get(`/api/tenants/${tenantId}/access-requests${query}`);
    return r.data;
  }

  /** Admin: approve or deny a request */
  async reviewRequest(
    tenantId: string,
    requestId: string,
    decision: 'approved' | 'denied',
    reviewNote?: string,
  ): Promise<AddonAccessRequest> {
    const r = await apiClient.put(
      `/api/tenants/${tenantId}/access-requests/${requestId}/review`,
      { decision, reviewNote },
    );
    return r.data;
  }

  /** Check whether the current user has add-on access for a given course type */
  async checkAddonAccess(courseType: 0 | 1): Promise<AddonAccessCheck> {
    const r = await apiClient.get(`/api/billing/user/addon-access-check?courseType=${courseType}`);
    return r.data;
  }
}

export const ADDON_DISPLAY_NAMES: Record<string, string> = {
  'Enterprise-OnDemand-Module-Yearly': 'On-Demand',
  'Enterprise-Webinar-Module-Yearly': 'Webinars',
  'OnDemand-Module-Yearly': 'On-Demand',
  'Webinars-Module-Yearly': 'Webinars',
};

export default new AddonAssignmentService();
